import { useLocation } from "wouter";
import { useCreateSession } from "@/api/auth/queries";
import { useValidateAuthCode } from "@/features/auth/hooks/use-validate-auth-code";
import { normalizeReturnTo } from "@/features/auth/utils/auth-state";
import { useEffect, useState } from "react";
import { Logo } from "@/components/ui/logo";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { AuthLoader } from "@/features/auth/components/auth-loader";
import { Button } from "@/components/ui/button";
import { Search } from "@/components/ui/search/search";

const AVATAR_COLORS = [
  "#F2318A", // pink
  "#019AA8", // teal
  "#FF9E01", // amber
  "#8636ED", // purple
  "#00C148", // green
  "#0F6CEA", // blue
  "#AF2780", // dark magenta
  "#C17700", // dark orange
  "#007635", // dark green
  "#5C13B9", // dark purple
  "#006F77", // dark teal
  "#FF0162", // red-pink
  "#290092", // navy
  "#F2994A", // peach
  "#00A851", // mid green
  "#615EB1", // muted purple
  "#105662", // slate teal
  "#6B9FEA", // light blue
];

const getAvatarColor = (index: number) => AVATAR_COLORS[index % AVATAR_COLORS.length];

/*
 * getCompanyInitials
 * Utility function to get company initials for the account avatar. It takes the first letter of the first two
 * words in the company name. If there's only one word, it takes the first two letters of that word.
 */
const getCompanyInitials = (company: string) => {
  const words = company
    .split(" ")
    .map((w) => w.replace(/^[^a-zA-Z]+/, ""))
    .filter((w) => w.length > 0);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
};

/*
 * SelectAccountRoute component
 *
 * Allow the user to select an account for the current session.
 * If there's only one account, select it by default.
 * After selecting an account, create a session and redirect to the appropriate page based on the returnTo URL or default landing page.
 */
export const SelectAccountRoute = () => {
  /* Stores */
  const [, setLocation] = useLocation();

  /* State */
  const [accountSearch, setAccountSearch] = useState("");

  /* Utils */
  const returnTo = normalizeReturnTo(history.state?.returnTo);

  /* Queries */
  const { mutate: createSession, isPending: isCreatingSession } = useCreateSession();

  /* Hooks */
  const { authRes, error, code } = useValidateAuthCode();

  /* Handlers */
  const handleAccountSelect = (accountId: string) => {
    if (!authRes) return;
    const userId = authRes.user.id;
    const idToken = authRes.idToken;

    createSession(
      { accountId, userId, idToken },
      {
        onSuccess: () => {
          // If there's a valid returnTo URL, redirect there. Otherwise, go to the default landing page.
          if (returnTo) {
            window.location.replace(returnTo);
            return;
          }

          setLocation("/", { replace: true });
        },
      },
    );
  };

  /* Side Effects */
  useEffect(() => {
    // if there's only one account, select it by default
    if (authRes && authRes.accounts.length === 1) {
      handleAccountSelect(authRes.accounts[0].id);
    }
  }, [authRes]);

  /* Loading */
  if (code && !error && !authRes) {
    return <AuthLoader loaderText="Loading accounts" />;
  }

  /* Creating session */
  if (isCreatingSession || (authRes && authRes.accounts.length === 1)) {
    return <AuthLoader loaderText="Creating session" />;
  }

  /* Account selection */
  if (authRes?.accounts && !isCreatingSession) {
    // Keep each account's original index so avatar colours stay stable while filtering
    const filteredAccounts = authRes.accounts
      .map((account, index) => ({ account, index }))
      .filter(({ account }) => account.company.toLowerCase().includes(accountSearch.trim().toLowerCase()));

    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="bg-base-200 p-6 border border-base-content/10 rounded-2xl w-[calc(100%-2rem)] max-w-[657px] drop-shadow-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Logo /> <h3 className="ml-1.5">Dragonfly</h3>
            </div>
            <div className="flex gap-4">
              <p className="text-base-content/50">{authRes.user.email}</p>
              <p className="text-secondary cursor-pointer hover:underline" onClick={() => setLocation("/auth/signout")}>
                Sign out
              </p>
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <h1 className="mt-6 mb-5">Select an account:</h1>
            <Search className="" placeholder="Search accounts" value={accountSearch} onChange={(e) => setAccountSearch(e.target.value)} aria-label="Search accounts" />
          </div>
          <div className="h-72 overflow-y-auto">
            {filteredAccounts.length === 0 ? (
              <p className="text-base-content/50 text-center mt-6">No accounts match your search.</p>
            ) : (
              filteredAccounts.map(({ account, index }) => (
                <div
                  key={account.id}
                  className="flex items-center border border-base-content/10 rounded-lg hover:border-base-content/20 p-3 hover:cursor-pointer my-2 bg-base-200 hover:bg-base-300 py-4 px-6"
                  onClick={() => handleAccountSelect(account.id)}
                >
                  <div className="flex items-center justify-center size-12 rounded-lg" style={{ backgroundColor: getAvatarColor(index) }}>
                    <span className="font-semibold">{getCompanyInitials(account.company)}</span>
                  </div>
                  <p className="ml-3">{account.company}</p>
                  <ChevronRightIcon className="size-5 ml-auto" />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  /* Error */
  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="bg-base-200 p-6 border border-base-content/10 rounded-2xl w-[calc(100%-2rem)] max-w-[657px] drop-shadow-2xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Logo /> <h3 className="ml-1.5">Dragonfly</h3>
          </div>
        </div>
        <div className="flex items-center justify-between mt-6">
          <div className="">
            <h3>Something went wrong</h3>
            <p className="text-base-content/70">We were unable to load your accounts. Please try signing in again.</p>
            <Button variant="primary" onClick={() => setLocation("/auth/signin")} className="mt-6">
              Sign in <ChevronRightIcon className="size-5" />
            </Button>
            <a className="btn btn-ghost no-animation rounded-lg shadow-none border-none cursor-pointer ml-2 mt-6" href="mailto:support@dragonflyai.co">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
